import { STREAM_FEATURES, supports } from '../../constants/streamTypeCapabilities'

// Which scoreboard sources an event may be switched to. The answer is always the server's.
//
// Editing a saved event, the backend has already resolved it: `supportedScoreboardSources` on the
// broadcast accounts for the court and the stream tier together. An empty array is a real answer — this court and tier feed nothing — so it is
// honoured rather than filled in from elsewhere.
//
// Creating one, the caller asks the server the same question about a game that does not exist yet
// and passes the answer in the broadcast's place. Until a court and a tier are both chosen there is
// nothing to ask about, and the picker falls back to what the court response says the court itself
// can feed — which cannot include Studio, since that depends on the tier. The rules are
// deliberately not re-derived here: duplicating them client-side is the thing the server-resolved
// list exists to prevent.

export const SCOREBOARD_SOURCE_OFF = 'Off'
export const SCOREBOARD_SOURCE_STUDIO = 'Studio'

// Enum order, so the picker keeps a stable shape as availability changes under it.
const ORDER = ['Camera', 'Reader', SCOREBOARD_SOURCE_STUDIO]

const LABELS = { Off: 'Off', Camera: 'Camera', Reader: 'Reader', Studio: 'Studio' }

const scoreboardSourceOptions = (broadcast, court, livestreamType) => {
  const resolved = broadcast?.supportedScoreboardSources
  // An array, even an empty one, is the server's answer; anything else means it never answered.
  const available = Array.isArray(resolved) ? resolved : court?.scoreboardSources || []

  // The resolved list was computed for the tier the event was saved with, and the tier is editable
  // in this very form. Studio is the one source that depends on it, and the backend rejects a
  // disallowed source rather than quietly downgrading it — so leaving Studio on offer after a drop
  // to Plus would fail the save. The narrowing reads the tier table the form already shares with
  // the backend; what the court itself can feed stays entirely server-side.
  const usable = available.filter(
    (source) =>
      source !== SCOREBOARD_SOURCE_STUDIO ||
      supports(livestreamType, STREAM_FEATURES.MANUAL_SCOREBOARD),
  )

  return [SCOREBOARD_SOURCE_OFF, ...ORDER.filter((source) => usable.includes(source))].map(
    (value) => ({ value, label: LABELS[value] ?? value }),
  )
}

// What an untouched event is shown as, mirroring the server's own default: the single source when
// that is the only thing this court and tier can feed, Off whenever there is a choice to make.
export const defaultScoreboardSource = (options) => {
  const sources = options.filter(({ value }) => value !== SCOREBOARD_SOURCE_OFF)
  return sources.length === 1 ? sources[0].value : SCOREBOARD_SOURCE_OFF
}

// Whether a change to the picker should go to the scoreboard-source endpoint instead of waiting
// for the form to be saved. A running stream can change source without restarting — the sidecar
// switches the input it forwards and the pod is patched in place — but the form itself is locked
// once the stream leaves Scheduled, so without this the control is simply dead while live.
//
// No tier check: every tier that is offered a choice of sources may make it live. Which sources
// are on offer is the separate question scoreboardSourceOptions answers, and that is where Studio
// stays behind Pro. Practices and unsaved events have no game id and so have nothing to patch.
export const canSwitchSourceLive = ({ isLive, orgId, gameId, canControl }) =>
  Boolean(isLive && orgId && gameId && canControl)

export default scoreboardSourceOptions
